import { MDSSpacings } from "@/mds/foundations";
import {
  formatDateString,
  generateRelativePrepositionAdverbialDateString,
  generateShortDateString,
} from "@/domains/date/date";
import { css } from "@/domains/emotion";
import {
  MDSButton,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { DataExportObservable } from "@/store/data-exports/DataExportObservable";
import { DateTime } from "luxon";
import styled from "@emotion/styled";

interface Props {
  dataExport: DataExportObservable;
}

export const ExportItem = ({ dataExport }: Props) => {
  const handleDownloadClick = async () => {
    try {
      const response = await api.get("/v2/data-exports/{data_export_id}/download-url", {
        params: { path: { data_export_id: dataExport.id } },
      });

      if (response.data?.url) {
        console.log("downloading file:", response.data.url);
        fileModule.downloadFileFromUrlDirectly({
          fileName: `mem-export-${generateShortDateString(DateTime.fromJSDate(dataExport.startedAt))}.zip`,
          downloadUrl: response.data.url,
          inTab: false,
        });
      } else {
        console.error("Failed to get download URL", response.error);
      }
    } catch (error) {
      console.error("Error downloading export", error);
    }
  };

  return dataExport.isRunning ? (
    <DownloadContainer>
      <div className={downloadIconContainerStyles}>
        <MDSIcon kind={MDSIconKind.Spinner} />
      </div>
      <div className={downloadDetailsStyles}>
        Export started{" "}
        {generateRelativePrepositionAdverbialDateString(DateTime.fromJSDate(dataExport.startedAt))}
      </div>
    </DownloadContainer>
  ) : (
    <DownloadContainer>
      <div className={downloadIconContainerStyles}>
        <MDSIcon
          kind={dataExport.didFail ? MDSIconKind.Danger : MDSIconKind.CheckCircleSolid}
          color={dataExport.didFail ? MDSIconColor.Danger : MDSIconColor.Positive}
        />
      </div>
      <div className={downloadDetailsStyles}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          <MDSTooltip
            config={{
              label: formatDateString(DateTime.fromJSDate(dataExport.startedAt), "MM/dd/yy h:mm a"),
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <MDSTooltipTrigger>
              {dataExport.didFail ? "Export failed" : "Exported"}{" "}
              {generateRelativePrepositionAdverbialDateString(
                DateTime.fromJSDate(dataExport.startedAt)
              )}
            </MDSTooltipTrigger>
          </MDSTooltip>
        </MDSText>
        <MDSButton onClick={handleDownloadClick} isDisabled={dataExport.didFail}>
          Download
        </MDSButton>
      </div>
    </DownloadContainer>
  );
};

const DownloadContainer = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing[0.5],
  width: "100%",
  "&:not(:last-child) > div:last-child": {
    borderBottom: `1px solid ${theme.colors.border.default}`,
  },
}));

const downloadIconContainerStyles = css({
  paddingBottom: MDSSpacings["4"],
  paddingTop: MDSSpacings["2"],
});

const downloadDetailsStyles = css({
  display: "flex",
  alignItems: "center",
  gap: MDSSpacings["3"],
  justifyContent: "space-between",
  paddingBottom: MDSSpacings["4"],
  paddingTop: MDSSpacings["2"],
  width: "100%",
});
